import { defineStore } from 'pinia'
import { supabase } from '@/services/supabase'
import { getAuthErrorMessage, isSupabaseAuthError } from '@/services/supabase-errors'
import type { User } from '@supabase/supabase-js'
import posthog from 'posthog-js'

interface AuthState {
  user: User | null
  loadingAuth: boolean
  error: string | null
  isInitializing: boolean
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    loadingAuth: false,
    error: null,
    isInitializing: true,
  }),

  getters: {
    isAuthenticated: (state) => state.user !== null,
    userId: (state) => state.user?.id,
    userEmail: (state) => state.user?.email,
    userMetadata: (state) => state.user?.user_metadata,
    userCreatedAt: (state) => state.user?.created_at,
    // TODO: Implement actual teacher role check from user metadata or profile
    isTeacher: (state) => state.user?.user_metadata?.is_teacher === true,
  },

  actions: {
    async signIn(email: string, password: string, persistSession: boolean = false) {
      this.loadingAuth = true
      this.error = null

      try {
        const { data, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        })

        if (signInError) throw signInError

        this.user = data.user

        posthog.identify(data.user.id, { email: data.user.email })
        posthog.capture('user_signed_in', { email: data.user.email })

        if (!persistSession) this.setupSessionClearListener()

        return data
      } catch (err) {
        const errorCode = isSupabaseAuthError(err) ? err.code : 'unknown'
        if (isSupabaseAuthError(err)) {
          this.error = getAuthErrorMessage(err.code)
        } else {
          this.error = 'Ha ocurrido un error inesperado. Por favor, inténtalo de nuevo más tarde.'
        }
        posthog.capture('user_sign_in_failed', { error_code: errorCode })
        throw err
      } finally {
        this.loadingAuth = false
      }
    },

    async signOut() {
      this.loadingAuth = true
      this.error = null

      try {
        const { error: signOutError } = await supabase.auth.signOut()

        if (signOutError) throw signOutError

        posthog.capture('user_signed_out')
        posthog.reset()
        this.user = null
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Sign out failed'
        throw err
      } finally {
        this.loadingAuth = false
      }
    },

    async initializeAuth() {
      this.isInitializing = true
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession()
        this.user = session?.user ?? null
      } catch (err) {
        console.error('Failed to initialize auth:', err)
        this.error = 'Failed to initialize authentication'
      } finally {
        this.isInitializing = false
      }
    },

    setupAuthListener() {
      supabase.auth.onAuthStateChange((_event, session) => {
        this.user = session?.user ?? null
      })
    },

    async refreshUser() {
      const { data, error: refreshError } = await supabase.auth.getUser()
      if (refreshError) {
        console.error('Failed to refresh user:', refreshError)
        return false
      }
      this.user = data.user
      return true
    },

    setupSessionClearListener() {
      const handleBeforeUnload = async () => {
        if (this.user) await supabase.auth.signOut()
      }

      window.addEventListener('beforeunload', handleBeforeUnload)
      window.addEventListener('unload', handleBeforeUnload)
    },
  },
})
